import React, { useContext } from 'react';
import { useParams } from 'react-router';

import styled from '@emotion/styled';

import useGetDriverLookup from '../../../../hooks/dashboard/driver-lookup/useGetDriverLookup';
import useHireDispatcher from '../../../../hooks/dashboard/driver-lookup/useHireDispatcher';
import {
  HireDispatcherDataType,
  HireDispatcherInitialValues,
  HireDispatcherSchema,
} from '../../../../models/dashboard/driver-lookup/hire-dispatcher';
import { hireDispatcherAction } from '../../../../redux/slices/hireDispatcherSlice';
import { useAppDisPatch } from '../../../../redux/store';
import { DRIVER_LOOKUP_INFO } from '../../../../routes/routeConstants';
import { FormSubmitButton } from '../../../Button/FormSubmitButton';
import { CustomDatePicker } from '../../../FormCom/CustomDatePicker';
import CustomSelectField from '../../../FormCom/CustomSelectField';
import { FormComponent } from '../../../FormCom/FormComponent';
import { TextInputField } from '../../../FormCom/FormField';
import NaijaPhoneInputField from '../../../FormCom/NaijaPhoneInputField';
import SingleDocumentUploadField from '../../../FormCom/SingleDocumentUploadField';
import Tooltip from '../../../Tooltip/Tooltip';
import { TranslatingContainerContext } from '../../../TranslatingContainer/TranslatingContainerProvider';
import { ModalContext } from '../../ModalContext';

const CreateDispatcher = () => {
  const modalContext = useContext(ModalContext);
  const dispatch = useAppDisPatch();
  const { getDriverLookup } = useGetDriverLookup();
  const { sliderIndex, setSliderIndexPos, resetSliderIndex } = useContext(
    TranslatingContainerContext,
  )!;
  const { hireDispatcher, loading } = useHireDispatcher();

  const { ninNumber } = useParams();

  // Re-fetching the lookup navigates to a fresh jsonData URL, which refreshes the parent page
  const onHireDriverIfDoesAlreadyBelongToACompany = (
    data: HireDispatcherDataType,
  ) => {
    hireDispatcher(data, () => {
      getDriverLookup(ninNumber, DRIVER_LOOKUP_INFO, () => {
        modalContext?.setShowModal(false);
        resetSliderIndex();
      });
    });
  };

  const onSubmit = (value: HireDispatcherDataType) => {
    dispatch(hireDispatcherAction.setHireDispatcherSlider(value));
    if (value.employmentType === 'Exclusive') {
      setSliderIndexPos(1);
    } else {
      onHireDriverIfDoesAlreadyBelongToACompany(value);
    }
  };

  const onCancelSubmit = () => {
    modalContext?.setShowModal(false);
  };

  return (
    <Container
      className={
        sliderIndex > 1
          ? 'inactive'
          : `${sliderIndex === 1 ? 'has-height' : ''}`
      }
    >
      <FormComponent
        initialValues={HireDispatcherInitialValues}
        schema={HireDispatcherSchema}
        onSubmit={onSubmit}
        className={'large-gap'}
      >
        <CustomDatePicker
          label='Set Start Date'
          name='startDate'
          id='startDate'
          placeholder='Set Start Date'
        />

        <NaijaPhoneInputField
          label='Phone number'
          name='phoneNumber'
          id='phoneNumber'
          type='text'
          placeholder='Phone number'
        />

        <SingleDocumentUploadField
          id='profilePicture'
          name='profilePicture'
          label='Upload Updated Professional Image of Driver'
          placeholder='Drag your file to start uploading'
          accept='.jpg,.jpeg,.png'
        />
        <CustomSelectField
          label={
            <div>
              Employment type:{' '}
              <Tooltip content='Gig workers are independent contractors who may provide services to multiple organizations. Exclusive staff are dedicated to your organization and are not expected to work for other employers.' />
            </div>
          }
          name='employmentType'
          id='employmentType'
          placeholder='Employment type'
          optionData={['Gig', ' Exclusive']}
        />

        <TextInputField
          label='House address'
          name='address'
          id='address'
          type='text'
          placeholder='House address'
        />

        <ButtonArea>
          <FormSubmitButton
            className='clear fill'
            onClick={onCancelSubmit}
            btnType='button'
          >
            Cancel
          </FormSubmitButton>
          <FormSubmitButton className='fill' loading={loading}>
            Hire Driver
          </FormSubmitButton>
        </ButtonArea>
      </FormComponent>
    </Container>
  );
};

export default CreateDispatcher;

const Container = styled.div`
  display: flex;
  height: 0px;

  &.inactive > * {
    display: none;
  }

  &.has-height {
    height: auto;
  }
`;

const ButtonArea = styled.div`
  display: flex;
  width: 100%;
  gap: 16px;
`;
